import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import {
  AccountState,
  BillingDocument,
  Customer,
  DailyAdSpendFee,
  InvoiceScheduling,
  Organization,
  Plan,
  Response,
} from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import {
  arrayGroupBy,
  AuthService,
  BillingService,
  OrganizationService,
  StatsService,
  UpgradePlanService,
  UserSelectionService,
} from "@front/m19-services";
import {
  BillingInfoComponent,
  BillingInfoFormComponent,
  BillingInfoFormValue,
  IButtonDirective,
  ICardComponent,
  ICardHeader,
  IIconComponent,
  IInputDirective,
  BillingDocumentsTableComponent,
  ISelectMenuImports,
  ISepaSetupComponent,
  ISepaSetupModalData,
  ITooltipDirective,
  LinkStripeCardModalComponent,
  ModalRef,
  ModalService,
  PaymentMethodsComponent,
  PlanCardComponent,
  SpinnerComponent,
  ToasterService,
  TotalAdSpendFee,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { OnboardingCtaComponent } from "@m19-board/containers/board-layout/board-sidebar/onboarding-cta.component";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { BillableAdSpendCardComponent } from "./billable-ad-spend-card/billable-ad-spend-card.component";
import {
  SelfServiceTransferModalComponent,
  SelfServiceTransferModalData,
} from "@m19-board/settings/billing-settings/self-service-transfer/self-service-transfer.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { environment } from "apps/m19-board/src/environments/environment";
import { combineLatestWith, Observable, of } from "rxjs";
import { AjaxError } from "rxjs/ajax";
import { filter, map, switchMap, tap } from "rxjs/operators";
import {
  BillableAdSpendDetailModalComponent,
  BillableAdSpendDetailModalData,
} from "./billable-ad-spend-detail-modal/billable-ad-spend-detail-modal.component";
import { FormsModule } from "@angular/forms";

@UntilDestroy()
@Component({
  selector: "app-billing-settings",
  templateUrl: "billing-settings.component.html",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    OnboardingCtaComponent,
    BillingInfoFormComponent,
    PlanCardComponent,
    PaymentMethodsComponent,
    BillingDocumentsTableComponent,
    BillingInfoComponent,
    ICardComponent,
    ICardHeader,
    SpinnerComponent,
    ISelectMenuImports,
    CommonModule,
    IInputDirective,
    FormsModule,
    IIconComponent,
    ITooltipDirective,
    BillableAdSpendCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingSettingsComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly billingService = inject(BillingService);
  private readonly modalService = inject(ModalService);
  private readonly statsService = inject(StatsService);
  private readonly layoutSelector = inject(LayoutSelectorService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly organizationService = inject(OrganizationService);
  private readonly upgradePlanService = inject(UpgradePlanService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly organizationsGroups$ = this.billingService.organizations$.pipe(
    combineLatestWith(this.billingService.organizationsWithBillingAccess$),
    map(([organizations, organizationsWithBillingAccess]) => {
      organizations = organizations.filter((org) =>
        organizationsWithBillingAccess.includes(org.organization.organizationId),
      );

      if (this.whiteLabel) {
        return organizations.filter(
          (x) =>
            x.isParentPpcBoard ||
            (x.hasSubscription() &&
              x.accountGroups
                .flatMap((ag) => ag.getAccountMarketplaces())
                .filter((am) => am.state == AccountState.BIDDER_ON).length > 0),
        );
      }

      return organizations;
    }),
  );

  private readonly organizations = toSignal(this.organizationsGroups$);

  protected readonly searchQuery = signal("");
  protected readonly filteredOrganizations = computed(() => {
    const query = this.searchQuery().toLowerCase();
    return (
      this.organizations()?.filter(
        (organization) => !query || organization.organizationName.toLowerCase().includes(query),
      ) ?? []
    );
  });
  readonly nextInvoice = signal<InvoiceScheduling | undefined>(undefined);

  readonly selectedOrganization = signal<OrganizationAccountGroups | undefined>(undefined);

  readonly poNumberLoading = signal(false);
  readonly poNumber = signal<string | undefined>(undefined);
  readonly poNumberInput = signal<string | undefined>(undefined);

  readonly customerLoading = signal(false);

  @ViewChild("updatePoNumberModal") updatePoNumberModal!: TemplateRef<any>;
  updatePoNumberModalRef: ModalRef<any> | undefined;

  @ViewChild("updateBillingDetailsModal") updateBillingDetailsModal!: TemplateRef<BillingInfoFormComponent>;
  modalRef: ModalRef<void, TemplateRef<BillingInfoFormComponent>> | undefined;

  stripePublishableKey = environment.stripePublicKey;

  readonly user = toSignal(this.authService.loggedUser$);
  readonly locale = computed(() => this.user()?.locale);
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$);
  readonly canDisplayBillingInfo = computed(() => {
    return !this.isSelfService() && (this.selectedOrganization()!.isParentPpcBoard || !this.whiteLabel);
  });

  whiteLabel = this.layoutSelector.getBoardType() !== BoardType.M19;

  readonly accountMarketplaces = computed(() =>
    this.selectedOrganization()!
      .accountGroups.flatMap((x) => x.resources)
      .filter((x) => x.state == AccountState.BIDDER_ON),
  );
  readonly billingPlan = computed(() => this.selectedOrganization()?.organization.billingPlan);
  readonly plan = computed(() => this.billingPlan()?.plan);
  readonly canMigrateToParentOrganization = computed(
    () =>
      this.plan() === Plan.SELF_SERVICE &&
      !!this.selectedOrganization()?.parentOrganizationWhenOnwer &&
      this.accountMarketplaces() &&
      this.accountMarketplaces().length > 0,
  );

  readonly isSelfService = computed(() => this.plan() === Plan.SELF_SERVICE);

  readonly parentOrgName = computed(() => {
    return this.selectedOrganization()!.parentOrganizationWhenOnwer?.customerName;
  });

  ngOnInit(): void {
    this.organizationsGroups$
      .pipe(
        filter((organizations) => organizations.length > 0),
        map((organizations) => organizations.find((o) => o.id === this.selectedOrganization()?.id) ?? organizations[0]),
        tap((organization) => {
          this.selectedOrganization.set(organization);
          this.poNumber.set(organization.organization.poNumber ?? undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
        switchMap((organization) => {
          if (organization.customerId === undefined) {
            return of(undefined);
          }
          return this.billingService.getInvoiceScheduling(organization.customerId);
        }),
      )
      .subscribe({
        next: (response) => {
          this.nextInvoice.set(response);
        },
      });
  }

  private readonly dailyAdSpendFees = toSignal(
    this.statsService.getDailyAdSpendFee().pipe(
      map((data: DailyAdSpendFee[]) => {
        if (!data) return new Map<number, DailyAdSpendFee[]>();
        return arrayGroupBy(data, (x: DailyAdSpendFee) => x.parentOrganizationId ?? x.organizationId!);
      }),
    ),
  );

  private readonly dailyDspAdSpendFees = toSignal(
    this.statsService.getDailyDspAdSpendFee().pipe(
      map((data: DailyAdSpendFee[]) => {
        if (!data) return new Map<number, DailyAdSpendFee[]>();
        return arrayGroupBy(data, (x: DailyAdSpendFee) => x.parentOrganizationId ?? x.organizationId!);
      }),
    ),
  );

  private readonly dailyDspM19SeatAdSpendFees = toSignal(
    this.statsService.getDailyDspM19SeatAdSpendFee().pipe(
      map((data: DailyAdSpendFee[]) => {
        if (!data) return new Map<number, DailyAdSpendFee[]>();
        return arrayGroupBy(data, (x: DailyAdSpendFee) => x.parentOrganizationId ?? x.organizationId!);
      }),
    ),
  );

  readonly feesByInvoiceId = computed(() => {
    const result = new Map<string, TotalAdSpendFee[]>();
    const adSpendFees: DailyAdSpendFee[] =
      this.dailyAdSpendFees()?.get(this.selectedOrganization()!.organization.organizationId!) ?? [];
    const dspAdSpendFees: DailyAdSpendFee[] =
      this.dailyDspAdSpendFees()?.get(this.selectedOrganization()!.organization.organizationId!) ?? [];
    const dspM19SeatAdSpendFees: DailyAdSpendFee[] =
      this.dailyDspM19SeatAdSpendFees()?.get(this.selectedOrganization()!.organization.organizationId!) ?? [];
    for (const fee of adSpendFees) {
      if (!fee.stripeInvoiceId) continue;
      if (!result.has(fee.stripeInvoiceId)) {
        result.set(fee.stripeInvoiceId, []);
      }
      const existing = result
        .get(fee.stripeInvoiceId)!
        .find((x) => x.accountId === fee.accountId! && x.marketplace === fee.marketplace!);
      if (existing) {
        existing.adSpendFee += fee.adSpendFee ?? 0;
      } else {
        result.get(fee.stripeInvoiceId)!.push({
          accountId: fee.accountId!,
          marketplace: fee.marketplace!,
          accountName: fee.accountName!,
          adSpendFee: fee.adSpendFee ?? 0,
          dspAdSpendFee: 0,
          dspM19SeatAdSpendFee: 0,
        });
      }
    }
    for (const fee of dspAdSpendFees) {
      if (!fee.stripeInvoiceId) continue;
      if (!result.has(fee.stripeInvoiceId)) {
        result.set(fee.stripeInvoiceId, []);
      }
      const existing = result
        .get(fee.stripeInvoiceId)!
        .find((x) => x.accountId === fee.accountId! && x.marketplace === fee.marketplace!);
      if (existing) {
        existing.dspAdSpendFee += fee.adSpendFee ?? 0;
      } else {
        result.get(fee.stripeInvoiceId)!.push({
          accountId: fee.accountId!,
          marketplace: fee.marketplace!,
          accountName: fee.accountName!,
          adSpendFee: 0,
          dspAdSpendFee: fee.adSpendFee ?? 0,
          dspM19SeatAdSpendFee: 0,
        });
      }
    }
    for (const fee of dspM19SeatAdSpendFees) {
      if (!fee.stripeInvoiceId) continue;
      if (!result.has(fee.stripeInvoiceId)) {
        result.set(fee.stripeInvoiceId, []);
      }
      const existing = result
        .get(fee.stripeInvoiceId)!
        .find((x) => x.accountId === fee.accountId! && x.marketplace === fee.marketplace!);
      if (existing) {
        existing.dspM19SeatAdSpendFee += fee.adSpendFee ?? 0;
      } else {
        result.get(fee.stripeInvoiceId)!.push({
          accountId: fee.accountId!,
          marketplace: fee.marketplace!,
          accountName: fee.accountName!,
          adSpendFee: 0,
          dspAdSpendFee: 0,
          dspM19SeatAdSpendFee: fee.adSpendFee ?? 0,
        });
      }
    }
    return result;
  });

  openUpdatePoNumberModal() {
    this.updatePoNumberModalRef = this.modalService.openModal(this.updatePoNumberModal, {
      modalTitle: this.translocoService.translate("subscription-card.po_number"),
      size: "sm",
    });
  }

  updatePoNumberModalRefClose() {
    this.updatePoNumberModalRef?.close();
  }

  updatePoNumber(deletePoNumber?: boolean) {
    this.poNumberLoading.set(true);
    const poNumber = deletePoNumber ? "null" : this.poNumberInput();

    this.organizationService
      .updatePoNumber(this.selectedOrganization()!.organization.organizationId!, poNumber!)
      .subscribe({
        next: (res: Response) => {
          const updatedOrg = res.entity as Organization;
          this.poNumber.set(updatedOrg.poNumber);

          this.toasterService.success(
            this.translocoService.translate(
              deletePoNumber ? "subscription-card.po_number_deleted" : "subscription-card.po_number_updated",
            ),
          );
          this.poNumberLoading.set(false);
          this.updatePoNumberModalRefClose();
        },
        error: (error: AjaxError) => {
          this.toasterService.error(
            this.translocoService.translate("subscription-card.po_number_update_failed"),
            error.name ?? this.translocoService.translate("common.error"),
          );
          this.poNumberLoading.set(false);
        },
      });
  }

  openAddIbanModal() {
    this.modalService.openModal<ISepaSetupModalData, void>(ISepaSetupComponent, {
      size: "md",
      modalTitle: this.translocoService.translate("billing-settings.add_an_iban"),
      data: {
        stripePublicKey: environment.stripePublicKey,
        customer: this.selectedOrganization()!.customer!,
        boardAgencyName: this.layoutSelector.getAgencyName(),
      },
    });
  }

  openAddCreditCardModal() {
    this.modalService.openModal(LinkStripeCardModalComponent, {
      modalTitle: this.translocoService.translate("billing-settings.add_a_credit_card"),
      data: {
        stripePublicKey: environment.stripePublicKey,
        customer: this.selectedOrganization()!.customer!,
      },
    });
  }

  openUpdateBillingDetailsModal() {
    this.modalRef = this.modalService.openModal<void, void, TemplateRef<BillingInfoFormComponent>>(
      this.updateBillingDetailsModal,
      {
        modalTitle: this.translocoService.translate(
          this.selectedOrganization()?.customer
            ? "billing-settings.update_billing_info"
            : "billing-settings.enter_billing_details",
        ),
        size: "sm",
      },
    );
  }

  openMigrateToParentOrganizationModal() {
    if (!this.selectedOrganization()) return;
    this.modalService.openModal<SelfServiceTransferModalData, void>(SelfServiceTransferModalComponent, {
      size: "lg",
      height: "fill",
      modalTitle: this.translocoService.translate("self-service-transfer.transfer_self_service_to_agency_management"),
      data: {
        childOrganization: this.selectedOrganization()!.organization,
        accountMarketplaces: this.accountMarketplaces(),
      },
    });
  }

  updateBillingDetails(billingFormValue: BillingInfoFormValue) {
    const customer: Customer = {
      ...(this.selectedOrganization()!.customer! || {}),
      ...{ ...billingFormValue, paymentId: undefined },
      m19UserId: this.user()!.userId,
    };

    this.customerLoading.set(true);
    let createOrUpdateCustomer: Observable<Customer>;
    if (this.selectedOrganization()!.customer) {
      createOrUpdateCustomer = this.upgradePlanService.updateCustomer(this.selectedOrganization()!, customer);
    } else {
      createOrUpdateCustomer = this.upgradePlanService.createCustomer(this.selectedOrganization()!, customer);
    }

    createOrUpdateCustomer.subscribe({
      next: () => {
        this.modalRef?.close();
        this.toasterService.success(this.translocoService.translate("billing-settings.billing_details_updated"));
        this.customerLoading.set(false);
      },
      error: (error) => {
        this.toasterService.error(error.response ? error.response.message : error.message);
        this.customerLoading.set(false);
      },
    });
  }

  openInvoiceDetailsModal(invoice: BillingDocument): void {
    const organizationId = this.selectedOrganization()!.organization.organizationId;
    const invoiceFees = (feesByOrganizationId: Map<number, DailyAdSpendFee[]> | undefined) =>
      feesByOrganizationId?.get(organizationId)?.filter((fee) => fee.stripeInvoiceId === invoice.stripeInvoiceId) ?? [];

    this.modalService.openModal<BillableAdSpendDetailModalData, void>(BillableAdSpendDetailModalComponent, {
      modalTitle: this.translocoService.translate("billing-settings.invoice_details"),
      size: "lg",
      data: {
        currency: invoice.currency,
        locale: this.locale(),
        exportFilePrefix: "invoice_details_" + invoice.issuedOn,
        adSpendFees: invoiceFees(this.dailyAdSpendFees()),
        dspAdSpendFees: invoiceFees(this.dailyDspAdSpendFees()),
        dspM19SeatAdSpendFees: invoiceFees(this.dailyDspM19SeatAdSpendFees()),
      },
    });
  }

  changeOrganization(organization: OrganizationAccountGroups | undefined) {
    if (!organization) return;
    this.selectedOrganization.set(organization);
    this.poNumber.set(organization.organization.poNumber ?? undefined);
  }
}
